import { workspaceFieldStyles } from '../common/workspaceSurface';
import React, { useEffect, useId, useRef, useState } from 'react';
import styled from 'styled-components';
import { AlertCircle, ArrowLeft, Check, FileText, Pencil, Plus, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { SystemPrompt } from '@auw/api-client';
import { useSystemPromptStore, type SystemPromptMode } from '@auw/stores';
import { Button } from '../common/Button';
import { Chip } from '../common/Chip';
import { Modal } from '../common/Modal';

export interface SystemPromptModalProps {
  open: boolean;
  onClose: () => void;
  mode: SystemPromptMode;
  centerPaddingLeft?: number;
  centerPaddingRight?: number;
}

export const SystemPromptModal: React.FC<SystemPromptModalProps> = ({
  open,
  onClose,
  mode,
  centerPaddingLeft = 0,
  centerPaddingRight = 0,
}) => {
  const { t } = useTranslation('common');
  const fieldId = useId();
  const contentRef = useRef<HTMLElement>(null);
  const {
    prompts, selectedPromptIds, isLoading, isSaving, error,
    fetchPrompts, savePrompt, deletePrompt, selectPrompt, clearError,
  } = useSystemPromptStore();
  const [editor, setEditor] = useState<{ id?: string; name: string; content: string } | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const busy = isLoading || isSaving;
  const selectedId = selectedPromptIds[mode];

  useEffect(() => {
    if (!open) return;
    setEditor(null);
    setDeletingId(null);
    void fetchPrompts();
  }, [open, mode, fetchPrompts]);

  useEffect(() => {
    const content = contentRef.current;
    if (!open || !content) return;
    const previousFocus = document.activeElement;
    content.focus();
    const containFocus = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;
      const controls = Array.from(content.querySelectorAll<HTMLElement>(
        'button:not(:disabled), input:not(:disabled), textarea:not(:disabled), [tabindex="0"]',
      ));
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (!first || !last) {
        event.preventDefault();
        content.focus();
      } else if (!content.contains(document.activeElement) || document.activeElement === content) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', containFocus);
    return () => {
      document.removeEventListener('keydown', containFocus);
      if (previousFocus instanceof HTMLElement && previousFocus.isConnected) previousFocus.focus();
    };
  }, [open]);

  const startEdit = (prompt?: SystemPrompt) => {
    clearError();
    setDeletingId(null);
    setEditor(prompt ? { id: prompt.id, name: prompt.name, content: prompt.content } : { name: '', content: '' });
  };

  const save = async (apply: boolean) => {
    if (!editor) return;
    const prompt = await savePrompt({ name: editor.name, content: editor.content }, editor.id);
    if (!prompt) return;
    setEditor(null);
    if (apply) {
      selectPrompt(mode, prompt.id);
      onClose();
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => { if (!isSaving) onClose(); }}
      title={t('systemPrompt.title', { defaultValue: '시스템 프롬프트' })}
      titleIcon={<FileText size={18} />}
      width="780px"
      closeOnBackdrop={!editor && !isSaving}
      centerPaddingLeft={centerPaddingLeft}
      centerPaddingRight={centerPaddingRight}
    >
      <Content ref={contentRef} tabIndex={-1} role="dialog" aria-modal="true" aria-label={t('systemPrompt.title', { defaultValue: '시스템 프롬프트' })}>
        <Description>
          {t('systemPrompt.description', {
            defaultValue: '자주 사용하는 지시를 저장하고 선택해 사용해보세요.',
          })}
        </Description>

        {error && (
          <ErrorBanner role="alert">
            <AlertCircle size={16} />
            <span>{error}</span>
            {!editor && (
              <Button type="button" variant="surface" size="sm" disabled={busy} onClick={() => void fetchPrompts()}>
                {t('systemPrompt.retry', { defaultValue: '다시 시도' })}
              </Button>
            )}
          </ErrorBanner>
        )}

        {editor ? (
          <EditorForm onSubmit={(event) => { event.preventDefault(); void save(false); }}>
            <Toolbar>
              <Button
                type="button"
                variant="surface"
                size="sm"
                leftIcon={<ArrowLeft size={15} />}
                disabled={isSaving}
                onClick={() => { setEditor(null); clearError(); }}
              >
                {t('systemPrompt.back', { defaultValue: '목록으로' })}
              </Button>
              <EditorTitle>
                {editor.id
                  ? t('systemPrompt.editTitle', { defaultValue: '프롬프트 수정' })
                  : t('systemPrompt.createTitle', { defaultValue: '새 프롬프트' })}
              </EditorTitle>
            </Toolbar>
            <Field>
              <Label htmlFor={`${fieldId}-name`}>{t('systemPrompt.name', { defaultValue: '이름' })}</Label>
              <NameInput
                id={`${fieldId}-name`}
                value={editor.name}
                onChange={(event) => setEditor({ ...editor, name: event.target.value })}
                placeholder={t('systemPrompt.namePlaceholder', { defaultValue: '예: 영어로 답변' })}
                autoFocus
                maxLength={100}
                required
                disabled={isSaving}
              />
            </Field>
            <Field>
              <Label htmlFor={`${fieldId}-content`}>{t('systemPrompt.content', { defaultValue: '프롬프트 내용' })}</Label>
              <PromptTextarea
                id={`${fieldId}-content`}
                value={editor.content}
                onChange={(event) => setEditor({ ...editor, content: event.target.value })}
                placeholder={t('systemPrompt.contentPlaceholder', { defaultValue: '예: 모든 답변을 영어로 작성해 줘. 핵심 내용을 먼저 설명하고 간결하게 답변해 줘.' })}
                maxLength={12000}
                required
                disabled={isSaving}
                aria-describedby={`${fieldId}-count`}
              />
              <Count id={`${fieldId}-count`}>{editor.content.length.toLocaleString()} / 12,000</Count>
            </Field>
            <Footer>
              <Button type="button" variant="surface" disabled={isSaving} onClick={() => { setEditor(null); clearError(); }}>
                {t('systemPrompt.cancel', { defaultValue: '취소' })}
              </Button>
              <Button type="submit" variant="surface" disabled={busy || !editor.name.trim() || !editor.content.trim()} isLoading={isSaving}>
                {t('systemPrompt.save', { defaultValue: '저장' })}
              </Button>
              <Button variant="surface" type="button" disabled={busy || !editor.name.trim() || !editor.content.trim()} onClick={() => void save(true)}>
                {t('systemPrompt.saveAndApply', { defaultValue: '저장 후 사용' })}
              </Button>
            </Footer>
          </EditorForm>
        ) : (
          <>
            <Toolbar>
              <LibraryLabel>{t('systemPrompt.library', { defaultValue: '내 프롬프트' })} <Count as="span">{prompts.length}</Count></LibraryLabel>
              <Button variant="surface" type="button" size="sm" leftIcon={<Plus size={15} />} disabled={busy} onClick={() => startEdit()}>
                {t('systemPrompt.create', { defaultValue: '새 프롬프트' })}
              </Button>
            </Toolbar>

            <PromptList aria-busy={isLoading}>
              <PromptCard $active={!selectedId}>
                <CardHeading>
                  <PromptName>{t('systemPrompt.none', { defaultValue: '사용 안 함' })}</PromptName>
                  {!selectedId && <SelectedChip icon={<Check size={12} />}>{t('systemPrompt.selected', { defaultValue: '선택됨' })}</SelectedChip>}
                </CardHeading>
                <CardBottom>
                  <Hint>{t('systemPrompt.noneDescription', { defaultValue: '에이전트의 기본 지시를 사용합니다.' })}</Hint>
                  <Button
                    type="button"
                    variant="surface"
                    size="sm"
                    aria-pressed={!selectedId}
                    disabled={busy || !selectedId}
                    onClick={() => { selectPrompt(mode, null); onClose(); }}
                  >
                    {t('systemPrompt.select', { defaultValue: '선택' })}
                  </Button>
                </CardBottom>
              </PromptCard>

              {isLoading && <EmptyState role="status">{t('systemPrompt.loading', { defaultValue: '프롬프트를 불러오는 중입니다…' })}</EmptyState>}
              {!isLoading && prompts.length === 0 && !error && (
                <EmptyState>{t('systemPrompt.empty', { defaultValue: '저장한 프롬프트가 없습니다. 새 프롬프트를 추가해 보세요.' })}</EmptyState>
              )}

              {prompts.map((prompt) => (
                <PromptCard key={prompt.id} $active={selectedId === prompt.id}>
                  <CardHeading>
                    <PromptName>{prompt.name}</PromptName>
                    {selectedId === prompt.id && <SelectedChip icon={<Check size={12} />}>{t('systemPrompt.selected', { defaultValue: '선택됨' })}</SelectedChip>}
                    <Actions>
                      <Button
                        type="button" variant="surface" size="icon" disabled={busy}
                        aria-label={t('systemPrompt.editNamed', { name: prompt.name, defaultValue: '{{name}} 수정' })}
                        onClick={() => startEdit(prompt)}
                      >
                        <Pencil size={15} />
                      </Button>
                      <Button
                        type="button" variant="surface" size="icon" disabled={busy}
                        aria-label={t('systemPrompt.deleteNamed', { name: prompt.name, defaultValue: '{{name}} 삭제' })}
                        onClick={() => { clearError(); setDeletingId(prompt.id); }}
                      >
                        <Trash2 size={15} />
                      </Button>
                    </Actions>
                  </CardHeading>
                  <Preview>{prompt.content}</Preview>
                  {deletingId === prompt.id ? (
                    <DeleteConfirmation>
                      <Hint>{t('systemPrompt.deleteConfirm', { defaultValue: '이 프롬프트를 삭제할까요? 사용 중인 모든 에이전트에서 선택이 해제됩니다.' })}</Hint>
                      <Actions>
                        <Button type="button" variant="surface" size="sm" disabled={isSaving} onClick={() => setDeletingId(null)}>
                          {t('systemPrompt.cancel', { defaultValue: '취소' })}
                        </Button>
                        <Button type="button" variant="surface" size="sm" isLoading={isSaving} onClick={async () => { if (await deletePrompt(prompt.id)) setDeletingId(null); }}>
                          {t('systemPrompt.delete', { defaultValue: '삭제' })}
                        </Button>
                      </Actions>
                    </DeleteConfirmation>
                  ) : (
                    <CardBottom>
                      <Hint>{t('systemPrompt.shared', { defaultValue: '모든 에이전트에서 사용 가능' })}</Hint>
                      <Button
                        type="button" variant="surface" size="sm"
                        aria-pressed={selectedId === prompt.id}
                        disabled={busy || selectedId === prompt.id}
                        onClick={() => { selectPrompt(mode, prompt.id); onClose(); }}
                      >
                        {selectedId === prompt.id
                          ? t('systemPrompt.inUse', { defaultValue: '사용 중' })
                          : t('systemPrompt.apply', { defaultValue: '사용하기' })}
                      </Button>
                    </CardBottom>
                  )}
                </PromptCard>
              ))}
            </PromptList>
            <Footer>
              <Button type="button" variant="surface" disabled={isSaving} onClick={onClose}>
                {t('systemPrompt.close', { defaultValue: '닫기' })}
              </Button>
            </Footer>
          </>
        )}
      </Content>
    </Modal>
  );
};

const SelectedChip = styled(Chip)`
  && {
    background: ${({ theme }) => theme.colors.brandSoft};
    border-color: ${({ theme }) => theme.colors.brand};
    color: ${({ theme }) => theme.colors.brand};
  }
`;

const Content = styled.section`
  display: flex;
  flex-direction: column;
  gap: 18px;
  outline: none;
`;
const Description = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 13px;
  line-height: 1.7;
`;
const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;
const LibraryLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
`;
const PromptList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: min(52vh, 480px);
  overflow-y: auto;
`;
const PromptCard = styled.div<{ $active: boolean }>`
  padding: 16px;
  border: 1px solid ${({ theme, $active }) => $active ? theme.colors.brand : theme.colors.border};
  border-radius: ${({ theme }) => theme.sizes.borderRadius};
  background: ${({ theme, $active }) => $active ? theme.colors.brandSoft : theme.colors.bgSecondary};
`;
const CardHeading = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;
const PromptName = styled.h3`
  margin: 0;
  min-width: 0;
  font-size: 14px;
  font-weight: 600;
  overflow-wrap: anywhere;
  color: ${({ theme }) => theme.colors.textPrimary};
`;
const Actions = styled.div`
  display: flex;
  flex-shrink: 0;
  gap: 4px;
  margin-left: auto;
`;
const CardBottom = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 12px;
`;
const Hint = styled.span`
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 12px;
  line-height: 1.6;
`;
const Preview = styled.p`
  margin: 8px 0 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 13px;
  line-height: 1.7;
`;
const EmptyState = styled.p`
  margin: 0;
  padding: 24px 12px;
  text-align: center;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 13px;
`;
const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 8px;
  padding-top: 16px;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;
const EditorForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 18px;
`;
const EditorTitle = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textSecondary};
`;
const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
const Label = styled.label`
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textPrimary};
`;
const NameInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 11px 12px;
  border: 1px solid ${({ theme }) => theme.colors.borderInput};
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  background: ${({ theme }) => theme.colors.bgInput};
  color: ${({ theme }) => theme.colors.textPrimary};
  font: inherit;
  font-size: 13px;
  &:focus { outline: 2px solid ${({ theme }) => theme.colors.brand}; outline-offset: 1px; }
  &::placeholder { color: ${({ theme }) => theme.colors.textMuted}; }
  ${workspaceFieldStyles}
`;
const PromptTextarea = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  min-height: 210px;
  max-height: 40vh;
  resize: vertical;
  padding: 12px;
  border: 1px solid ${({ theme }) => theme.colors.borderInput};
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  background: ${({ theme }) => theme.colors.bgInput};
  color: ${({ theme }) => theme.colors.textPrimary};
  font: inherit;
  font-size: 13px;
  line-height: 1.7;
  &:focus { outline: 2px solid ${({ theme }) => theme.colors.brand}; outline-offset: 1px; }
  &::placeholder { color: ${({ theme }) => theme.colors.textMuted}; }
  ${workspaceFieldStyles}
`;
const Count = styled.div`
  color: ${({ theme }) => theme.colors.textMuted};
  text-align: right;
  font-size: 11px;
  font-weight: 400;
`;
const ErrorBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border: 1px solid ${({ theme }) => theme.colors.error};
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  color: ${({ theme }) => theme.colors.error};
  font-size: 13px;
  line-height: 1.5;
  > svg { flex-shrink: 0; }
  > span { flex: 1; overflow-wrap: anywhere; }
`;
const DeleteConfirmation = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;
